import React from 'react';
import useIsMobile from '../hooks/useIsMobile';

/**
 * ModalCloseButton — a touch-friendly × button rendered in the header
 * of any modal/popover. Walter 2026-05-12: settings + several other
 * popups had either no X at all or a tiny 12-px-glyph X that's
 * un-tappable on mobile. This component standardises the close
 * affordance:
 *
 *   - Desktop: ~22px X inside a 24-px hit area (matches the prior
 *     visual weight).
 *   - Mobile : ~16px X inside a 40-px hit area (Apple's 44pt
 *     recommendation is generous for a header glyph; 40 lands
 *     comfortably).
 *
 * Renders absolutely positioned in the top-right of its container by
 * default. Pass `style` to override (e.g. inline in a header flex).
 *
 *   <ModalCloseButton onClick={onClose} />
 *   <ModalCloseButton onClick={onClose} style={{ position: 'static' }} />
 */
export default function ModalCloseButton({
  onClick,
  title = 'close (esc)',
  // When set, takes precedence over the default top-right absolute layout.
  style,
}) {
  const isMobile = useIsMobile();
  const hit = isMobile ? 40 : 28;
  const glyph = isMobile ? 16 : 12;
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label="close"
      style={{
        // Default: floating top-right inside a positioned container.
        // Callers that want it inline pass `style: { position: 'static' }`.
        position: 'absolute',
        top: isMobile ? 8 : 10,
        right: isMobile ? 8 : 10,
        width: hit,
        height: hit,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: 'var(--text-dimmer)',
        borderRadius: '6px',
        padding: 0,
        WebkitTapHighlightColor: 'transparent',
        ...style,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-dimmer)'; }}
    >
      <svg width={glyph} height={glyph} viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    </button>
  );
}
